import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Form, Pagination, Row, Select, Space, Table, Typography, message, Modal as AntModal, Col, Checkbox } from "antd";
import type { PaginationProps, TableProps } from "antd";
import "./InformationTable.css";
import { BsPencilSquare, BsTrashFill } from "react-icons/bs";
import { usePagination } from "src/hook/usePagination";
import { useDidMount } from "src/hook/useDidMount";
import { useShallow } from "zustand/react/shallow";
import { CalendarOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { replaceUndefinedWithNull, trimAllKey } from "src/utils/utils-object";
import { CommonSuccess } from "src/entity/common/menus-enum.enum";
import { uniqBy, isNil } from "lodash";
import InformationModal from "./InformationModal";
import { profileStore } from "src/stores/profile-store";
import VocsModal from "src/components/Modal";
import { setupWasteWaterStore } from "src/stores/waste-water-info-store";
import {
  CreateSetupWasteWater,
  FilterSetupWasteWater,
  ResponseSetupWasteWater,
  UpdateSetupWasteWaterDto,
  WasteWaterEmissionDTO,
} from "src/entity/waste-water/setup-information.entity";
import { utilsStore } from "src/stores/utils-store";
import NumberRow from "./NumberRow";
import { convertedDateRangesWithoutTime, covertedRangesDateToString } from "src/utils/date-time";
import { RangeDateDto } from "src/entity/flare/flare-volume.entity";
import VerticalCenter from "src/components/WrapCenter/VerticalCenter";
import { processEmissionObject } from "src/entity/waste-water/waste-water.constant";
import { ReportStatusEnum } from "src/entity/report-voc-emission/report-voc-emission.entity";
import useNavigationMenu from "src/hook/useNavigationMenu";
import { canEditTable, canViewTable } from "../../PermissionCheck/PermissionCheck";
import { reportWasteWaterStore } from "src/stores/waste-water-report";
import { convertEmissionWord } from "src/entity/common/global.entity";
import { optionsSelectYears } from "src/utils/array";
import { EmissionObject, EmissionObjectWithAll } from "src/entity/common/unit.constant";
import { addCenterAlign } from "src/components/AntdCustomPart/TableTitle.tsx/TableTitle";

const { Text } = Typography;
interface InformationTableProps {}

const InformationTable: React.FC<InformationTableProps> = ({}) => {
  const currentYear = new Date().getFullYear();
  const [messageApi, contextHolder] = message.useMessage();
  const [formSetupWasteWater] = Form.useForm();

  const [selectedYear, setSelectedYear] = useState<string>(currentYear.toString());
  const [visibleModal, setVisibleModal] = useState(false);
  const [isEdit, setIsEdit] = useState<ResponseSetupWasteWater | null>(null);
  const [tableKey, setTableKey] = useState(0);

  const { permissions } = useNavigationMenu();

  const profile = profileStore(useShallow((state) => state.profile));
  const getAreaInCompany = utilsStore(useShallow((state) => state.getAreaInCompany));
  const dataAreaInCompany = utilsStore(useShallow((state) => state.dataAreaInCompany));
  const getAllSettingWasteWater = setupWasteWaterStore(useShallow((state) => state.getAllSettingWasteWater));
  const dataSettingWasteWater = setupWasteWaterStore(useShallow((state) => state.dataSettingWasteWater));
  const searchSetupWasteWater = setupWasteWaterStore(useShallow((state) => state.searchSetupWasteWater));
  const dataWasteWater = setupWasteWaterStore(useShallow((state) => state.dataWasteWater));
  const totalDocs = dataWasteWater.total;
  const getFilterColumnSetupWasteWater = setupWasteWaterStore(useShallow((state) => state.getFilterColumnSetupWasteWater));
  const filterWasteWater = setupWasteWaterStore(useShallow((state) => state.filterWasteWater));
  const loadingManageWasteWater = setupWasteWaterStore(useShallow((state) => state.loadingManageWasteWater));
  const createSetupWasteWater = setupWasteWaterStore(useShallow((state) => state.createSetupWasteWater));
  const deleteSetupWasteWater = setupWasteWaterStore(useShallow((state) => state.deleteSetupWasteWater));
  const updateSetupWasteWater = setupWasteWaterStore(useShallow((state) => state.updateSetupWasteWater));
  const getMasterEmissionObject = setupWasteWaterStore(useShallow((state) => state.getMasterEmissionObject));
  const dataMasterEmissionObject = setupWasteWaterStore(useShallow((state) => state.dataMasterEmissionObject));
  const clearError = setupWasteWaterStore(useShallow((state) => state.clearError));
  const errorWasteWater = setupWasteWaterStore(useShallow((state) => state.errorWasteWater));

  const calculateReportVocWasteWater = reportWasteWaterStore(useShallow((state) => state.calculateReportVocWasteWater));

  // console.log(" filterWasteWater : ", filterWasteWater);

  const getMasterLabel = (type: string, text: string) => {
    if (!text || typeof text != "string") return "";

    if (type === "emissionControl" && dataWasteWater) {
      const optValue = dataSettingWasteWater.find((item) => item._id === text);
      return optValue?.emissionControl;
    } else if (type === "efficiency" && dataWasteWater) {
      const optValue = dataSettingWasteWater.find((item) => item._id === text);
      return optValue?.efficiency;
    } else if (type === "area" && dataAreaInCompany) {
      const optValue = dataAreaInCompany.find((item) => item._id === text);
      return optValue?.areaName;
    } else return text;
  };

  const onClickEdit = (item: ResponseSetupWasteWater) => {
    const emissionLists = [];
    for (const key in item.emission) {
      if (item.emission[key as keyof WasteWaterEmissionDTO].active) emissionLists.push(key);
    }
    const controlExceptionDate = covertedRangesDateToString(item.controlExceptionDate as RangeDateDto[]);
    formSetupWasteWater.setFieldsValue({
      wasteWaterUnit: item.wasteWaterUnit,
      area: getMasterLabel("area", item.area) ? item.area : undefined,
      emissionControl: item?.emissionControl?._id && item?.emissionControl?.isActive ? item?.emissionControl?._id : undefined,
      efficiency: item?.emissionControl?.isActive ? item.emissionControl.efficiency : !isNil(item.efficiency) ? item.efficiency : "",
      controlExceptionDate,
      emission: emissionLists,
    });
    setIsEdit(item);
    setVisibleModal(true);
  };

  const deleteSetting = async (id: string) => {
    const confirm = await VocsModal.delete({
      message: "",
    });
    if (!confirm) return;
    deleteSetupWasteWater(id).then(() => {
      onFinishAction();
    });
  };

  const {
    onChange,
    filter,
    page,
    setPage,
    loading,
    limit: pageSize,
    data: sourceData,
  } = usePagination<ResponseSetupWasteWater, FilterSetupWasteWater>({
    action: searchSetupWasteWater,
    remoteData: dataWasteWater.data,
    initialFilter: { year: [selectedYear] },
  });

  const wasteWaterFilter = useMemo(() => {
    if (dataWasteWater.data?.length === 0) return [];
    return filterWasteWater.wasteWaterUnit.map((item) => {
      return {
        text: item,
        value: item,
      };
    });
  }, [filterWasteWater.wasteWaterUnit, dataWasteWater.data]);
  const areaFilter = useMemo(() => {
    if (dataWasteWater.data?.length === 0) return [];
    return filterWasteWater.area.map((item) => {
      const findLabel = dataAreaInCompany.find((e) => e._id === item);
      return {
        text: findLabel?.areaName,
        value: item,
      };
    });
  }, [filterWasteWater.area, dataWasteWater.data, dataAreaInCompany]);
  const emissionControlFilter = useMemo(() => {
    if (dataWasteWater.data?.length === 0) return [];
    return filterWasteWater.emissionControl.map((item) => {
      const findLabel = dataSettingWasteWater.find((e) => e._id === item);
      return {
        text: findLabel?.emissionControl,
        value: item,
      };
    });
  }, [filterWasteWater.emissionControl, dataWasteWater.data, dataSettingWasteWater]);

  const efficiencyFilter = useMemo(() => {
    if (dataWasteWater.data?.length === 0) return [];
    return filterWasteWater.efficiency.map((item) => {
      return {
        text: item,
        value: item,
      };
    });
  }, [filterWasteWater.efficiency, dataWasteWater.data]);

  const filterControlExceptionDate = useMemo(() => {
    if (dataWasteWater.data?.length === 0) return [];
    const response = filterWasteWater.controlExceptionDate.flatMap((item) => {
      if (item.length > 0)
        return item.flatMap((ele) => {
          return {
            text: `${ele.startDate} > ${ele.endDate}`,
            value: `${ele.startDate} > ${ele.endDate}`,
          };
        });
      else {
        return {
          text: ``,
          value: `empty_string > empty_string`,
        };
      }
    });
    return uniqBy(response, "value");
  }, [filterWasteWater.controlExceptionDate, dataWasteWater.data]);

  const handleTableChange: TableProps<ResponseSetupWasteWater>["onChange"] = (_, filters) => {
    onChange(filters);
  };

  const renderPeriodRange = (ranges: RangeDateDto[]) => {
    if (!ranges || !ranges?.length) return <></>;

    return (
      <Row>
        <Col>
          {ranges.map((item, index) => {
            return (
              <Row key={index + "period"} className={`bg-white-500 border rounded-md ${index !== 0 ? "mt-2" : ""}`}>
                <Text className="p-1">
                  {item.startDate}
                  {" -> "}
                  {item.endDate}
                </Text>
              </Row>
            );
          })}
        </Col>
        <Col className="pl-2">
          <VerticalCenter>
            <CalendarOutlined style={{ fontSize: 18 }} />
          </VerticalCenter>
        </Col>
      </Row>
    );
  };

  const getIndex = (index: number) => (page - 1) * pageSize + index;
  const columns: TableProps<ResponseSetupWasteWater>["columns"] = [
    {
      title: "Wastewater Unit",
      dataIndex: "wasteWaterUnit",
      key: "wasteWaterUnit",
      render: (text) => <Text>{text}</Text>,
      filters: wasteWaterFilter,
    },
    {
      title: "Area",
      dataIndex: "area",
      key: "area",
      render: (text) => <Text>{getMasterLabel("area", text)}</Text>,
      filters: areaFilter,
    },
    {
      title: "Emission Control",
      dataIndex: "emissionControl",
      key: "emissionControl",
      render: (_, record) => <Text>{record?.emissionControl?.isActive ? record.emissionControl.emissionControl : ""}</Text>,
      filters: emissionControlFilter,
    },
    {
      title: "Efficiency (%)",
      dataIndex: "efficiency",
      key: "efficiency",
      render: (_, record) => (
        <Text>
          {record?.emissionControl?.isActive ? record.emissionControl.efficiency : !isNil(record.efficiency) ? record.efficiency : ""}
        </Text>
      ),
      filters: efficiencyFilter,
    },
    // {
    //   title: "Control Exception Date",
    //   dataIndex: "controlExceptionDate",
    //   key: "controlExceptionDate",
    //   render: (_, record) => renderPeriodRange(record.controlExceptionDate as RangeDateDto[]),
    //   filters: filterControlExceptionDate,
    //   align: "left",
    // },
    // {
    //   title: "Emission",
    //   children:
    //     dataMasterEmissionObject.length > 0
    //       ? dataMasterEmissionObject[0].options.map((item) => ({
    //           title: convertEmissionWord(item.label),
    //           dataIndex: "emission",
    //           width: "3rem",
    //           align: "left",
    //           render: (value) => (
    //             <div>
    //               <Checkbox checked={value[item.value].active} disabled={true}></Checkbox>
    //             </div>
    //           ),
    //         }))
    //       : [],
    //   align: "center",
    // },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space size="middle" direction={"horizontal"}>
          <Button
            className="border-transparent bg-transparent"
            size={"small"}
            onClick={() => onClickEdit(record)}
            disabled={!canEditTable(permissions)}
          >
            <BsPencilSquare size={18} />
          </Button>
          <Button
            className="border-transparent bg-transparent"
            size={"small"}
            onClick={() => deleteSetting(record._id)}
            disabled={
              !canEditTable(permissions) ||
              JSON.stringify(record.vocEmission).includes(ReportStatusEnum.REQUEST) ||
              JSON.stringify(record.vocEmission).includes(ReportStatusEnum.APPROVER_1) ||
              JSON.stringify(record.vocEmission).includes(ReportStatusEnum.APPROVER_2) ||
              JSON.stringify(record.vocEmission).includes(ReportStatusEnum.APPROVER_3)
            }
          >
            <BsTrashFill size={18} />
          </Button>
        </Space>
      ),
    },
  ];

  const getColumnFilter = () => {
    getFilterColumnSetupWasteWater(selectedYear);
  };

  const initialRequestDidMount = (isDidMount?: boolean) => {
    getAreaInCompany(profile?.activeCompany._id as string);
    getAllSettingWasteWater();
    getColumnFilter();
    searchSetupWasteWater(
      isDidMount === true ? { ...filter, page, limit: pageSize, year: [selectedYear] } : { page, limit: pageSize, year: [selectedYear] },
    );
    getMasterEmissionObject();
  };

  useDidMount(() => {
    initialRequestDidMount(true);
  });

  useEffect(() => {
    if (profile?.activeCompany) {
      setPage(1);
      setTableKey(tableKey + 1);
    }
  }, [profile?.activeCompany, selectedYear]);
  useEffect(() => {
    initialRequestDidMount(false);
  }, [tableKey]);

  const onChangePage = (val: number) => {
    setPage(val);
  };

  const paginationProps: PaginationProps = {
    size: "small",
    pageSize: pageSize,
    current: page,
    total: totalDocs || 0,
    defaultCurrent: 1,
    onChange: onChangePage,
    showSizeChanger: false,
  };

  const onAddSetupWasteWater = () => {
    formSetupWasteWater.setFieldValue("emission", ["tvoc"]);
    setVisibleModal(true);
  };

  const closeModal = () => {
    setVisibleModal(false);
    formSetupWasteWater.resetFields();
    clearError("errorWasteWater");
    setIsEdit(null);
  };

  const mapEmissionObject = (emission: string[], isEdit: ResponseSetupWasteWater | null) => {
    // console.log("XXXXX emission :: ", emission);
    const responseObject = !isEdit?.emission ? { ...processEmissionObject } : isEdit.emission;
    EmissionObject.forEach((item) => {
      if (emission.includes(item.value))
        responseObject[`${item.value as keyof WasteWaterEmissionDTO}`] = {
          ...responseObject[`${item.value as keyof WasteWaterEmissionDTO}`],
          active: true,
        };
      else
        responseObject[`${item.value as keyof WasteWaterEmissionDTO}`] = {
          ...responseObject[`${item.value as keyof WasteWaterEmissionDTO}`],
          active: false,
        };
    });

    return responseObject;
  };

  const submitSetupWasteWater = async () => {
    let checkError = false;
    await formSetupWasteWater.validateFields().catch(() => {
      checkError = true;
    });
    if (checkError) return;

    const formData = formSetupWasteWater.getFieldsValue();
    // console.log("form data ; ", formData);
    const formatData = trimAllKey(replaceUndefinedWithNull(formData), []);
    const finalData = {
      ...formatData,
      companyId: profile?.activeCompany._id as string,
      year: selectedYear,
      isActive: true,
      emission: mapEmissionObject(formatData.emission, isEdit),
      controlExceptionDate: formatData.controlExceptionDate ? convertedDateRangesWithoutTime(formatData.controlExceptionDate) : [],
    };

    // console.log("Final data : ", finalData);
    if (isEdit) {
      updateSetupWasteWater({ ...finalData, _id: isEdit._id } as UpdateSetupWasteWaterDto).then(onFinishAction);
    } else createSetupWasteWater(finalData as CreateSetupWasteWater).then(onFinishAction);
  };

  const handleChangeYear = (year: string) => {
    // console.log("Year : ", year);
    onChange({ ...filter, year: [year] });
    setSelectedYear(year);
  };

  const onFinishAction = (response?: unknown) => {
    // console.log("response : ", response);
    if (typeof response === "string") {
      // warningAlert(response as string);
      formSetupWasteWater.setFields([
        {
          name: "gasName",
          errors: [""],
        },
        {
          name: "composition",
          errors: [""],
        },
      ]);
      return;
    } else {
      if (isEdit?._id) calculateReportVocWasteWater(isEdit._id);
      messageApi.open({
        type: "success",
        content: CommonSuccess.SAVED_SUCCESSFULL,
      });
      getColumnFilter();
      searchSetupWasteWater({ ...filter, page, limit: pageSize, year: [selectedYear] });
      closeModal();
    }
  };

  const processRowClassName = useCallback(
    (record: ResponseSetupWasteWater) => {
      const hasArea = dataAreaInCompany.find((item) => item._id === record.area);
      return !hasArea ? "red-border-row" : "";
    },
    [selectedYear, sourceData, dataAreaInCompany],
  );

  return (
    <Row className="w-full" id={"wrap-waste-water-table"}>
      {contextHolder}
      <Row className="w-full" justify={"space-between"}>
        <Select value={selectedYear} style={{ width: 120 }} onChange={handleChangeYear} options={optionsSelectYears()} size="middle" />
        <Button
          type={"primary"}
          size={"middle"}
          className="flex items-center"
          onClick={onAddSetupWasteWater}
          style={canEditTable(permissions) ? {} : { display: "none" }}
        >
          <PlusCircleOutlined />
          New
        </Button>
      </Row>
      <Table
        key={tableKey}
        columns={addCenterAlign(columns)}
        dataSource={canViewTable(permissions) ? (sourceData as ResponseSetupWasteWater[]) : []}
        className="w-full mt-4"
        loading={loading}
        pagination={false}
        onChange={handleTableChange}
        scroll={{ x: "max-content" }}
        rowKey={(rec) => `${rec._id}`}
        rowClassName={processRowClassName} // Apply red border conditionally
      />

      <Row className="w-full mt-4" justify={"end"}>
        <Pagination {...paginationProps} />
      </Row>

      {visibleModal && (
        <InformationModal
          dataAreaInCompany={dataAreaInCompany}
          dataSettingWasteWater={dataSettingWasteWater}
          submitSetupWasteWater={submitSetupWasteWater}
          loadingManageWasteWater={loadingManageWasteWater}
          visibleModal={visibleModal}
          closeModal={closeModal}
          errorWasteWater={errorWasteWater}
          formSetupWasteWater={formSetupWasteWater}
          clearErrorWasteWater={clearError}
        />
      )}
    </Row>
  );
};

export default InformationTable;
